import { useState } from 'react'
import { useMutation } from 'urql'
import { gql } from '../gql/gql'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog'

const CHANGE_PASSWORD_MUTATION = gql(`
  mutation ChangePassword($currentPassword: String!, $newPassword: String!) {
    changePassword(currentPassword: $currentPassword, newPassword: $newPassword)
  }
`)

const SET_PASSWORD_MUTATION = gql(`
  mutation SetPassword($newPassword: String!) {
    setPassword(newPassword: $newPassword)
  }
`)

interface ChangePasswordDialogProps {
  children?: React.ReactNode
  hasPassword: boolean
}

export function ChangePasswordDialog({ children, hasPassword }: ChangePasswordDialogProps) {
  const [open, setOpen] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const [{ fetching: changing }, changePassword] = useMutation(CHANGE_PASSWORD_MUTATION)
  const [{ fetching: setting }, setPassword] = useMutation(SET_PASSWORD_MUTATION)
  const fetching = changing || setting

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess(false)

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match')
      return
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    const result = hasPassword
      ? await changePassword({ currentPassword, newPassword })
      : await setPassword({ newPassword })
    if (result.error) {
      setError(result.error.message)
      return
    }

    setSuccess(true)
    setTimeout(() => setOpen(false), 1200)
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setError('')
      setSuccess(false)
    }
    setOpen(isOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{hasPassword ? 'Change Password' : 'Set Password'}</DialogTitle>
          <DialogDescription>
            {hasPassword ? 'Enter your current password to set a new one.' : 'Add a password to your account.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-3 pt-1">
          {error && <p className="text-sm text-destructive">{error}</p>}
          {success && <p className="text-sm text-green-600">Password {hasPassword ? 'updated' : 'set'}.</p>}
          {hasPassword && (
            <div className="space-y-1">
              <Label htmlFor="currentPassword">Current password</Label>
              <Input id="currentPassword" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
            </div>
          )}
          <div className="space-y-1">
            <Label htmlFor="newPassword">New password</Label>
            <Input id="newPassword" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
          </div>
          <div className="space-y-1">
            <Label htmlFor="confirmPassword">Confirm new password</Label>
            <Input id="confirmPassword" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
          </div>
          <Button type="submit" className="w-full" disabled={fetching}>
            {fetching ? 'Saving...' : hasPassword ? 'Update password' : 'Set password'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}
